import { Component, OnInit } from '@angular/core';
import Swal from 'sweetalert2';
import { SupplyService } from '../supply.service';

@Component({
  selector: 'app-supply-dispatch',
  templateUrl: './supply-dispatch.component.html',
  styleUrls: ['./supply-dispatch.component.scss']
})
export class SupplyDispatchComponent implements OnInit {
  loading = false;
  saving = false;
  editingId: number | null = null;

  productTypes: any[] = [];
  productItems: any[] = [];
  dispatchRows: any[] = [];

  displayedColumns = [
    'dispatchDate',
    'product',
    'buyer',
    'route',
    'units',
    'pricing',
    'totalAmount',
    'actions'
  ];

  form: any = {
    productTypeId: null,
    productItemId: null,
    buyerName: '',
    buyerContact: '',
    origin: '',
    destination: '',
    unitCount: null,
    unitPrice: null,
    commissionRate: 0,
    dispatchDate: '',
    notes: ''
  };

  constructor(private supplyService: SupplyService) {}

  ngOnInit(): void {
    this.loadPageData();
  }

  loadPageData(): void {
    this.loading = true;
    this.supplyService.getProducts().subscribe({
      next: (types) => {
        this.productTypes = types || [];
        this.rebindItems();
        this.loadDispatchRows();
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load product types.', 'error');
      }
    });
  }

  loadDispatchRows(): void {
    this.supplyService.getDispatches().subscribe({
      next: (rows) => {
        this.dispatchRows = rows || [];
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load dispatch history.', 'error');
      }
    });
  }

  onTypeChange(): void {
    this.form.productItemId = null;
    this.rebindItems();
  }

  get commissionAmount(): number {
    const unitCount = Number(this.form.unitCount || 0);
    const unitPrice = Number(this.form.unitPrice || 0);
    const rate = Number(this.form.commissionRate || 0);
    return unitCount * unitPrice * (rate / 100);
  }

  get totalAmount(): number {
    const unitCount = Number(this.form.unitCount || 0);
    const unitPrice = Number(this.form.unitPrice || 0);
    return unitCount * unitPrice;
  }

  get netAmount(): number {
    return this.totalAmount - this.commissionAmount;
  }

  saveDispatch(): void {
    if (!this.form.productTypeId || !this.form.productItemId || !String(this.form.buyerName || '').trim()) {
      Swal.fire('Required', 'Product type, item and buyer name are required.', 'info');
      return;
    }

    const payload = {
      productTypeId: Number(this.form.productTypeId),
      productItemId: Number(this.form.productItemId),
      buyerName: String(this.form.buyerName || '').trim(),
      buyerContact: String(this.form.buyerContact || '').trim() || null,
      origin: String(this.form.origin || '').trim() || null,
      destination: String(this.form.destination || '').trim() || null,
      unitCount: Number(this.form.unitCount || 0),
      unitPrice: Number(this.form.unitPrice || 0),
      commissionRate: Number(this.form.commissionRate || 0),
      dispatchDate: this.form.dispatchDate || new Date().toISOString(),
      notes: String(this.form.notes || '').trim() || null
    };

    this.saving = true;
    const request$ = this.editingId
      ? this.supplyService.updateDispatch(this.editingId, payload)
      : this.supplyService.createDispatch(payload);

    request$.subscribe({
      next: () => {
        this.saving = false;
        Swal.fire('Success', this.editingId ? 'Dispatch updated.' : 'Dispatch saved.', 'success');
        this.resetForm();
        this.loadDispatchRows();
      },
      error: (err) => {
        this.saving = false;
        Swal.fire('Error', err?.error?.message || 'Failed to save dispatch.', 'error');
      }
    });
  }

  editRow(row: any): void {
    this.editingId = row.id;
    this.form = {
      productTypeId: row.productTypeId,
      productItemId: row.productItemId,
      buyerName: row.buyerName || '',
      buyerContact: row.buyerContact || '',
      origin: row.origin || '',
      destination: row.destination || '',
      unitCount: row.unitCount,
      unitPrice: row.unitPrice,
      commissionRate: row.commissionRate,
      dispatchDate: this.toInputDate(row.dispatchDate),
      notes: row.notes || ''
    };
    this.rebindItems();
  }

  async deleteRow(row: any): Promise<void> {
    const result = await Swal.fire({
      title: 'Delete Dispatch?',
      text: `This will delete dispatch entry for ${row.buyerName}.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete'
    });
    if (!result.isConfirmed) return;

    this.supplyService.deleteDispatch(row.id).subscribe({
      next: () => {
        Swal.fire('Deleted', 'Dispatch entry removed.', 'success');
        if (this.editingId === row.id) {
          this.resetForm();
        }
        this.loadDispatchRows();
      },
      error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to delete dispatch.', 'error')
    });
  }

  resetForm(): void {
    this.editingId = null;
    this.form = {
      productTypeId: null,
      productItemId: null,
      buyerName: '',
      buyerContact: '',
      origin: '',
      destination: '',
      unitCount: null,
      unitPrice: null,
      commissionRate: 0,
      dispatchDate: '',
      notes: ''
    };
    this.productItems = [];
  }

  private rebindItems(): void {
    const selected = this.productTypes.find(t => t.id === Number(this.form.productTypeId));
    this.productItems = selected?.items || [];
  }

  private toInputDate(value: string): string {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
